import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../../reducers';
import {
	Wrapper,
	Header,
	Body,
	Title,
	ButtonCombine,
	ButtonSelectAll,
	Loader,
} from './styles';
import { HeaderButton } from '../../Header/styles';
import ButtonZoom from '../../ButtonZoom';
import Grid from '../../../../containers/Editor/CombinePanel/SpreadsSelector/Grid';
import Spinner from '../../Spinner';
import {
	getExternalPublicationData,
	resetExternalPublicationData,
	selectExternalSpread,
	selectAllExternalSpreads,
	deselectAllExternalSpreads,
	createComboWithExternalSpreads,
	zoomExternalSpreads,
} from '../../../../containers/Editor/actions';
import { EditorMessageTypes, ZoomType } from '../../../../containers/Editor/constants';
import Utils from '../../../../utils';

interface SpreadsSelectorProps {
	resourceId: number;
	isLoading: boolean;
	isCombining: boolean;
	spreads: number[][];
	selectedSpreadIndices: number[];
	isSelectedAll: boolean;
	onClose: () => void;
	dispatch: Dispatch<StoreState>;
}

const SpreadsSelector = ({
	resourceId,
	isLoading,
	isCombining,
	selectedSpreadIndices,
	isSelectedAll,
	spreads,
	onClose,
	dispatch,
}: SpreadsSelectorProps) => {
	const bodyElement = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		dispatch(getExternalPublicationData(resourceId));

		return () => {
			dispatch(resetExternalPublicationData());
		};
	}, [resourceId]);

	const handleItemClick = (index: number, isCtrlPressed?: boolean, isShiftPressed?: boolean) => {
		dispatch(selectExternalSpread(index, spreads, isCtrlPressed, isShiftPressed));
	};

	const handleSelectAllClick = () => {
		if (isSelectedAll) {
			dispatch(deselectAllExternalSpreads());
		} else {
			dispatch(selectAllExternalSpreads());
		}
	};

	const handleCombineClick = () => {
		dispatch(createComboWithExternalSpreads(() => {
			// Reload resource in react-admin after adding reference/s to an empty playlist in the Editor
			if (Utils.isEmptyPlaylistMode()) {
				const parentOrigin = new URL(document.referrer).origin;
				window.parent.postMessage(EditorMessageTypes.RELOAD_PUBLICATION_SETTINGS, parentOrigin);
			}
		}));
	};

	const handleZoomClick = (zoom: ZoomType) => {
		if (bodyElement.current) {
			const grid = bodyElement.current.firstElementChild as Element;
			const gridItem = grid.firstElementChild as Element;
			const { width: gridWidth } = grid.getBoundingClientRect();
			const { width } = gridItem.getBoundingClientRect();
			const { marginLeft, marginRight } = window.getComputedStyle(gridItem);
			const margin = parseFloat(marginLeft as string) + parseFloat(marginRight as string);
			const itemsInRow = Math.floor(gridWidth / (width + margin));

			dispatch(zoomExternalSpreads(zoom, itemsInRow));
		}
	};

	const combineButtonLabel = Utils.isPlaylistMode() ? 'messages.add' : 'messages.combine';

	return (
		<Wrapper>
			<Header>
				<HeaderButton
					iconWidth="10px"
					iconHeight="10px"
					onClick={onClose}
				>
					{Utils.getSvgIcon('iconset_close-2')}
				</HeaderButton>
				<Title value="messages.combine_by_selecting_pages" />
				<ButtonSelectAll
					disabled={isLoading}
					onClick={handleSelectAllClick}
				>
					<Translate value={`messages.${isSelectedAll ? 'deselect_all' : 'select_all'}`} />
				</ButtonSelectAll>
				<ButtonZoom
					isDisabled={isLoading || isCombining}
					onClick={handleZoomClick}
				/>
				<ButtonCombine
					disabled={selectedSpreadIndices.length === 0 || isCombining}
					onClick={handleCombineClick}
				>
					<Translate value={combineButtonLabel} />
				</ButtonCombine>
			</Header>
			<Body ref={bodyElement}>
				{isLoading
					? (
						<Loader>
							<Spinner />
							<Translate value="messages.loading" />
						</Loader>
					)
					: <Grid onItemClick={handleItemClick} />
				}
				{isCombining && (
					<Loader>
						<Spinner />
						<Translate
							value="messages.combining"
							percentage="0"
						/>
					</Loader>
				)}
			</Body>
		</Wrapper>
	);
};

export default SpreadsSelector;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/CombinePanel/SpreadsSelector/index.tsx